import { createRoute, redirect as rawRedirect, lazyRouteComponent } from '@tanstack/react-router';

// Wrapper to satisfy @typescript-eslint/only-throw-error – TanStack Router's
// redirect() returns a plain object designed to be thrown; the router catches it.
const redirect: (...args: Parameters<typeof rawRedirect>) => Error = rawRedirect as never;
import { rootRoute } from '@/routes/index';
import { usePortalAuthStore } from '../stores/portalAuthStore';
import { PortalLayout } from '../layouts/PortalLayout';
import { PORTAL_BOOKKEEPING_AVAILABLE } from '../config/portalFeatures';
import PortalBookkeepingUnavailablePage from '../pages/PortalBookkeepingUnavailablePage';

// Auth guard for portal authenticated routes
const portalAuthGuard = () => {
  const isAuthenticated = usePortalAuthStore.getState().isAuthenticated;
  if (!isAuthenticated) {
    throw redirect({ to: '/portal/login' });
  }
};

// ── Portal root route ────────────────────────────────────────────────
const portalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal',
  beforeLoad: () => {
    // Redirect /portal to /portal/dashboard
    const pathname = window.location.pathname;
    if (pathname === '/portal' || pathname === '/portal/') {
      throw redirect({ to: '/portal/dashboard' });
    }
  },
});

// ── Public portal routes (no auth required) ──────────────────────────
const portalLoginRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/login',
  component: lazyRouteComponent(() => import('../pages/PortalLoginPage')),
});

const portalRegisterRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/register',
  component: lazyRouteComponent(() => import('../pages/PortalRegisterPage')),
});

const portalMfaRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/mfa',
  component: lazyRouteComponent(() => import('../pages/PortalMFAPage')),
});

const portalMagicLinkRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/magic-link',
  component: lazyRouteComponent(() => import('../pages/MagicLinkVerifyPage')),
});

const portalResetPasswordRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: '/reset-password',
  component: lazyRouteComponent(() => import('../pages/PasswordResetPage')),
});

// ── Authenticated layout route ───────────────────────────────────────
const portalAuthLayoutRoute = createRoute({
  getParentRoute: () => portalRoute,
  id: 'portal-auth-layout',
  component: PortalLayout,
});

// ── Authenticated portal routes ──────────────────────────────────────
const portalDashboardRoute = createRoute({
  getParentRoute: () => portalAuthLayoutRoute,
  path: '/dashboard',
  component: lazyRouteComponent(() => import('../pages/PortalDashboardPage')),
  beforeLoad: portalAuthGuard,
});

const portalDocumentsRoute = createRoute({
  getParentRoute: () => portalAuthLayoutRoute,
  path: '/documents',
  component: lazyRouteComponent(() => import('../pages/PortalDocumentsPage')),
  beforeLoad: portalAuthGuard,
});

const portalDocumentViewRoute = createRoute({
  getParentRoute: () => portalAuthLayoutRoute,
  path: '/documents/$id',
  component: lazyRouteComponent(() => import('../pages/PortalDocumentViewPage')),
  beforeLoad: portalAuthGuard,
});

const portalChatRoute = createRoute({
  getParentRoute: () => portalAuthLayoutRoute,
  path: '/chat',
  component: lazyRouteComponent(() => import('../pages/PortalChatPage')),
  beforeLoad: portalAuthGuard,
});

const portalChatRoomRoute = createRoute({
  getParentRoute: () => portalAuthLayoutRoute,
  path: '/chat/$roomId',
  component: lazyRouteComponent(() => import('../pages/PortalChatRoomPage')),
  beforeLoad: portalAuthGuard,
});

const portalNotificationsRoute = createRoute({
  getParentRoute: () => portalAuthLayoutRoute,
  path: '/notifications',
  component: lazyRouteComponent(() => import('../pages/PortalNotificationsPage')),
  beforeLoad: portalAuthGuard,
});

const portalSettingsRoute = createRoute({
  getParentRoute: () => portalAuthLayoutRoute,
  path: '/settings',
  component: lazyRouteComponent(() => import('../pages/PortalSettingsPage')),
  beforeLoad: portalAuthGuard,
});

const portalMfaSetupRoute = createRoute({
  getParentRoute: () => portalAuthLayoutRoute,
  path: '/settings/mfa-setup',
  component: lazyRouteComponent(() => import('../pages/PortalMFASetupPage')),
  beforeLoad: portalAuthGuard,
});

const portalReconciliationRoute = createRoute({
  getParentRoute: () => portalAuthLayoutRoute,
  path: '/reconciliation',
  component: lazyRouteComponent(() => import('../pages/PortalReconciliationPage')),
  beforeLoad: portalAuthGuard,
});

const portalReconciliationDetailRoute = createRoute({
  getParentRoute: () => portalAuthLayoutRoute,
  path: '/reconciliation/$shareToken',
  component: lazyRouteComponent(() => import('../pages/PortalReconciliationDetailPage')),
  beforeLoad: portalAuthGuard,
});

const portalTransactionsRoute = createRoute({
  getParentRoute: () => portalAuthLayoutRoute,
  path: '/bookkeeping',
  component: PORTAL_BOOKKEEPING_AVAILABLE
    ? lazyRouteComponent(() => import('../pages/PortalTransactionsPage'))
    : PortalBookkeepingUnavailablePage,
  beforeLoad: portalAuthGuard,
});

const portalReportsRoute = createRoute({
  getParentRoute: () => portalAuthLayoutRoute,
  path: '/reports',
  component: PORTAL_BOOKKEEPING_AVAILABLE
    ? lazyRouteComponent(() => import('../pages/PortalReportsPage'))
    : PortalBookkeepingUnavailablePage,
  beforeLoad: portalAuthGuard,
});

const portalPayrollRoute = createRoute({
  getParentRoute: () => portalAuthLayoutRoute,
  path: '/payroll',
  component: lazyRouteComponent(() => import('../pages/PortalPayrollPage')),
  beforeLoad: portalAuthGuard,
});

const portalTaxTimelineRoute = createRoute({
  getParentRoute: () => portalAuthLayoutRoute,
  path: '/tax-timeline',
  component: lazyRouteComponent(() => import('../tax-timeline/TaxTimelinePage')),
  beforeLoad: portalAuthGuard,
});

// ── Build the portal route tree ──────────────────────────────────────
export const portalRouteTree = portalRoute.addChildren([
  portalLoginRoute,
  portalRegisterRoute,
  portalMfaRoute,
  portalMagicLinkRoute,
  portalResetPasswordRoute,
  portalAuthLayoutRoute.addChildren([
    portalDashboardRoute,
    portalDocumentsRoute,
    portalDocumentViewRoute,
    portalChatRoute,
    portalChatRoomRoute,
    portalNotificationsRoute,
    portalSettingsRoute,
    portalMfaSetupRoute,
    portalReconciliationRoute,
    portalReconciliationDetailRoute,
    portalTransactionsRoute,
    portalReportsRoute,
    portalPayrollRoute,
    portalTaxTimelineRoute,
  ]),
]);
